<script setup>
// FLOW-13 (SPEC S1, R7) — a lista do "Usado por": cada ferramenta com o ícone do tipo, o nome, os
// agentes dela e o link. A mesma lista abre na linha do segredo, vai nos diálogos de "Trocar valor" e
// de "Excluir", e na lista dos nomes que ficaram nos agentes.
//
// O link: o flow e a ferramenta HTTP abrem no editor (`powerflow_flow_editor`, pelo id da entrada). A
// tool antiga não tem editor próprio: ela mora na aba Ferramentas do agente, e o link vai para lá
// (`power_agents_settings`, pelo `agente_id` que o "Usado por" traz, com `?secao=tools`). Sem o id do
// agente, ela entra sem link, e o nome do agente ao lado diz onde ela mora.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import IconeDaTool from '../powerAgents/sections/IconeDaTool.vue';
import { humanizar, listaDeNomes } from './usoDoSegredo';

defineProps({
  // `[{chave, tipo, id, nome, agentes}]`, de `ferramentasDoUso`.
  ferramentas: { type: Array, default: () => [] },
  // Dentro de um diálogo ou de um aviso, a lista ganha a própria borda.
  comBorda: { type: Boolean, default: false },
});

const { locale } = useI18n();
const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);

const ehDoEditor = ferramenta =>
  ferramenta.tipo === 'flow' || ferramenta.tipo === 'ferramenta_http';
const temLink = ferramenta =>
  ehDoEditor(ferramenta) ||
  (ferramenta.tipo === 'legada' && ferramenta.agenteId != null);

const rotaDe = ferramenta =>
  ehDoEditor(ferramenta)
    ? {
        name: 'powerflow_flow_editor',
        params: { accountId: accountId.value, id: ferramenta.id },
      }
    : {
        name: 'power_agents_settings',
        params: { accountId: accountId.value, agentId: ferramenta.agenteId },
        query: { secao: 'tools' },
      };

const agentesDe = ferramenta => listaDeNomes(ferramenta.agentes, locale.value);
</script>

<template>
  <ul
    class="flex flex-col gap-0.5 m-0 list-none"
    :class="comBorda ? 'p-1 border rounded-lg border-n-weak' : 'p-0'"
    data-lista-de-uso
  >
    <li
      v-for="ferramenta in ferramentas"
      :key="ferramenta.chave"
      data-uso
      :data-tipo="ferramenta.tipo"
    >
      <component
        :is="temLink(ferramenta) ? 'router-link' : 'div'"
        v-bind="temLink(ferramenta) ? { to: rotaDe(ferramenta) } : {}"
        class="flex items-center min-w-0 gap-2.5 px-2.5 py-1.5 text-sm rounded-lg"
        :class="
          temLink(ferramenta)
            ? 'transition-colors duration-150 hover:bg-n-alpha-2 focus-visible:outline-2 focus-visible:outline-n-brand'
            : ''
        "
      >
        <IconeDaTool
          pequeno
          :flow="ferramenta.tipo === 'flow'"
          :http="ferramenta.tipo === 'ferramenta_http'"
        />
        <span class="font-medium truncate text-n-slate-12" data-nome-do-uso>
          {{ humanizar(ferramenta.nome) }}
        </span>
        <template v-if="ferramenta.agentes.length">
          <span
            class="rounded-full size-[3px] shrink-0 bg-n-slate-10"
            aria-hidden="true"
          />
          <span class="truncate text-n-slate-11" data-agentes-do-uso>
            {{ agentesDe(ferramenta) }}
          </span>
        </template>
        <span
          v-if="temLink(ferramenta)"
          class="i-lucide-chevron-right size-4 ms-auto shrink-0 text-n-slate-10"
          aria-hidden="true"
        />
      </component>
    </li>
  </ul>
</template>
